/**
 * Fetches every listing row for promo rule dropdowns (categories, brands, products).
 * "Select all" must not only use the first paginated page (e.g. limit 10).
 */
const DEFAULT_PAGE_SIZE = 10;
/** Safety cap so one promo rule cannot pull unbounded rows into the payload. */
export const PROMO_SELECT_ALL_MAX = 2000;

/**
 * @param {object} options
 * @param {Function} options.dispatch - Redux dispatch
 * @param {Function} options.fetchThunk - createAsyncThunk action (e.g. getPlatformCategory)
 * @param {object} options.params - API params (platform, searchTerm, etc.)
 * @param {Function} options.mapItem - maps API row to rule selectedValue shape
 * @param {number} [options.pageSize]
 * @returns {Promise<{ items: Array, total: number, capped: boolean }>}
 */
export async function fetchAllListingItems({
  dispatch,
  fetchThunk,
  params,
  mapItem,
  pageSize = DEFAULT_PAGE_SIZE,
}) {
  const first = await dispatch(
    fetchThunk({ ...params, skip: 0, limit: pageSize })
  ).unwrap();

  const total = Number(first?.total ?? 0);
  const firstData = Array.isArray(first?.data) ? first.data : [];

  if (total === 0) {
    return { items: [], total: 0, capped: false };
  }

  const cap = Math.min(total, PROMO_SELECT_ALL_MAX);
  let rows = firstData;

  if (total > firstData.length) {
    if (cap <= pageSize) {
      rows = firstData;
    } else {
      const bulk = await dispatch(
        fetchThunk({ ...params, skip: 0, limit: cap })
      ).unwrap();
      rows = Array.isArray(bulk?.data) ? bulk.data : firstData;
    }

    if (rows.length < cap && cap > pageSize) {
      let skip = rows.length;
      const merged = [...rows];
      while (merged.length < cap && skip < cap) {
        const page = await dispatch(
          fetchThunk({
            ...params,
            skip,
            limit: Math.min(pageSize, cap - skip),
          })
        ).unwrap();
        const chunk = Array.isArray(page?.data) ? page.data : [];
        if (chunk.length === 0) break;
        merged.push(...chunk);
        skip += chunk.length;
        if (chunk.length < pageSize) break;
      }
      rows = merged;
    }
  }

  const items = rows.map(mapItem).filter(Boolean);
  return {
    items,
    total,
    capped: total > PROMO_SELECT_ALL_MAX,
  };
}

/** Merge listing items into existing multi-select array (dedupe by idKey). */
export function mergeListingSelection(existing, incoming, idKey) {
  const base = Array.isArray(existing) ? existing : [];
  const seen = new Set(base.map((item) => item?.[idKey]).filter(Boolean));
  const added = incoming.filter((item) => {
    const id = item?.[idKey];
    if (!id || seen.has(id)) return false;
    seen.add(id);
    return true;
  });
  return [...base, ...added];
}

/** Remove items whose id is in scopeIds (from last "select all" operation). */
export function removeListingSelectionByIds(existing, scopeIds, idKey) {
  if (!Array.isArray(existing)) return existing;
  const scope = new Set(scopeIds.map((id) => String(id)).filter(Boolean));
  if (scope.size === 0) return existing;
  return existing.filter((item) => !scope.has(String(item?.[idKey])));
}

/** True when every visible row is already in the current selection. */
export function isVisibleListingFullySelected(inputValue, visibleItems, idKey) {
  if (!Array.isArray(inputValue) || !visibleItems?.length) return false;
  return visibleItems.every((row) =>
    inputValue.some((item) => String(item?.[idKey]) === String(row?.[idKey]))
  );
}

/** True when scope ids from last "select all" are all still selected. */
export function isScopeFullySelected(inputValue, scopeIds, idKey) {
  if (!Array.isArray(inputValue) || !scopeIds?.length) return false;
  return scopeIds.every((id) =>
    inputValue.some((item) => String(item?.[idKey]) === String(id))
  );
}

/**
 * Whether the select-all checkbox should appear checked (including after dropdown remount).
 */
export function isListingSelectAllChecked({
  inputValue,
  visibleItems,
  idKey,
  scopeIds = [],
  selectAllActive = false,
  total = 0,
}) {
  if (!Array.isArray(inputValue) || inputValue.length === 0) return false;
  if (isScopeFullySelected(inputValue, scopeIds, idKey)) return true;
  if (selectAllActive && isVisibleListingFullySelected(inputValue, visibleItems, idKey)) {
    return true;
  }
  if (total > 0 && inputValue.length >= total) {
    return isVisibleListingFullySelected(inputValue, visibleItems, idKey);
  }
  return isVisibleListingFullySelected(inputValue, visibleItems, idKey);
}

/**
 * Whether the next select-all click should clear (not re-fetch and add).
 */
export function shouldClearListingSelectAll({
  inputValue,
  visibleItems,
  idKey,
  scopeIds = [],
  selectAllActive = false,
  total = 0,
}) {
  return isListingSelectAllChecked({
    inputValue,
    visibleItems,
    idKey,
    scopeIds,
    selectAllActive,
    total,
  });
}
